import React, { useEffect, useState } from 'react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { toJS } from 'mobx';
import Url from 'environment';
import { Background } from '../styled';
import { Wrap, Header, CloseButton, ContentWrap, Input, ButtonsWrap, Button, AuthorSelectContainer } from './styled';
import CommentatorSelect from 'components/commentator-select';
import { NamedCommunityService } from 'services/named';
import { ScoreUtil } from '../../../../../common/utils/scoreUtil';
import useUsersStore from '../../../../../hooks/useUsersStore';
import ReCAPTCHA from 'react-google-recaptcha';

const Editor = (props) => {
  const { data, setData, gameId, view, setView, isModify, getAnalysisList, sportsType, recaptchaVerify } = props;
  const [editor, setEditor] = useState();
  const [subject, setSubject] = useState('');
  const [writer, setWriter] = useState(null);

  const userStore = useUsersStore();
  const userData = toJS(userStore.data);

  const recaptchaRef = React.useRef(null);

  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (recaptchaRef.current) {
        setIsReady(true);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    let editorInstance;
    ClassicEditor.create(document.getElementById('editor'), {
      placeholder: !!props.placeholder ? props.placeholder : '내용을 입력하세요.',
      mediaEmbed: {
        previewsInData: true,
      },
    })
      .then((editor) => {
        editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
          loader.read = () => Promise.resolve();
          return new UploadAdapter(loader, recaptchaVerify);
        };

        setEditor(editor);
        editorInstance = editor;
      })
      .catch((error) => {
        console.log(error);
      });
    return () => {
      if (editorInstance) {
        editorInstance.destroy().catch((e) => console.warn('Editor destroy error:', e));
      }
    };
  }, []);

  useEffect(() => {
    if (!!editor) {
      if (isModify && !!data) {
        setSubject(data.wr_subject);
        editor.setData(data.wr_content);
      } else {
        setSubject('');
        editor.setData('');
      }
    }
  }, [editor, isModify, data]);

  const onSelectWriter = (selectedWriter) => {
    setWriter(selectedWriter);
  };

  const onClickSubmit = async (e) => {
    e.preventDefault();
    const content = editor.getData();

    let formData = {
      gameId: gameId,
      boardType: 'sports_analysis',
      subject,
      content,
      sportsId: ScoreUtil.getSportsId(sportsType),
      board_writer_id: writer?.ID,
    };

    if (!subject) {
      alert('제목을 입력하세요.');
      return;
    }
    if (!content) {
      alert('내용을 입력하세요.');
      return;
    }
    const res = isModify
      ? await NamedCommunityService.modify({ ...formData, id: data.wr_id })
      : await NamedCommunityService.save(formData);

    if (res.code === 200) {
      alert('분석글이 등록되었습니다.');
      setView(false);
      getAnalysisList();
      if (!isModify) {
        setSubject('');
        editor.setData('');
      } else {
        setData(res.data);
      }
    } else {
      alert(res.message);
    }
  };

  return (
    <Background view={view} onClick={() => setView(false)}>
      <Wrap onClick={(e) => e.stopPropagation()}>
        <Header>
          <h2>{`분석글 ${isModify ? '편집' : '작성'}`}</h2>
          <CloseButton onClick={() => setView(false)} />
        </Header>
        <ContentWrap>
          <AuthorSelectContainer>
            {userData?.is_assignable ? <CommentatorSelect onSelectWriter={onSelectWriter} /> : null}
          </AuthorSelectContainer>
          <Input
            type={'text'}
            placeholder='제목을 입력하세요.'
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <div id='editor' />
        </ContentWrap>
        <ButtonsWrap>
          <p>이용약관 및 영창/수감규정 정책에 위배되는 경우, 이용에 제한을 받을 수 있습니다.</p>
          <Button className={'cancel'} onClick={() => setView(false)}>
            취소
          </Button>
          <Button className={'submit'} onClick={onClickSubmit}>
            등록
          </Button>
        </ButtonsWrap>
      </Wrap>
    </Background>
  );
};

class UploadAdapter {
  constructor(loader, recaptchaVerify) {
    this.loader = loader;
    this.recaptchaVerify = recaptchaVerify;
    this.xhr = null;
  }

  upload() {
    return new Promise(async (resolve, reject) => {
      const token = await this.recaptchaVerify?.();
      if (!token) {
        return reject('reCAPTCHA failed');
      }
      const file = await this.loader.file;
      this._initRequest();
      this._initListeners(resolve, reject, file);
      this._sendRequest(file, token);
    });
  }

  abort() {
    if (this.xhr) {
      this.xhr.abort();
    }
  }

  _initRequest() {
    const xhr = (this.xhr = new XMLHttpRequest());
    xhr.open('POST', `${Url.named}community/board/upload-file`, true);
    xhr.responseType = 'json';
  }

  async _initListeners(resolve, reject, file) {
    const xhr = this.xhr;
    const loader = this.loader;
    const genericErrorText = `Couldn't upload file: ${file.name}.`;

    xhr.addEventListener('error', () => reject(genericErrorText));
    xhr.addEventListener('abort', () => reject());
    xhr.addEventListener('load', () => {
      const response = xhr.response;

      if (!response || response.error || response.code === 400) {
        return reject(response?.error?.message || response?.message || genericErrorText);
      }

      resolve({
        default: response.url,
      });
    });

    if (xhr.upload) {
      xhr.upload.addEventListener('progress', (evt) => {
        if (evt.lengthComputable) {
          loader.uploadTotal = evt.total;
          loader.uploaded = evt.loaded;
        }
      });
    }
  }

  _sendRequest(file, recaptchaToken) {
    const data = new FormData();
    data.append('file', file);
    data.append('g-recaptcha-response', recaptchaToken);
    this.xhr.send(data);
  }
}

export default Editor;
